import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../../../contexts/AuthContext";
import { toast } from "react-toastify";
import { contractService } from "../../../services/api/contractService";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import MobilePatternHeader from "../components/MobilePatternHeader";

const ListSignDocument = ({ isMobile }) => {
  const navigate = useNavigate();
  const { token_client } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();

  // ---- state
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search") || ""
  );
  const [debounced, setDebounced] = useState(searchParams.get("search") || "");
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);

  const shouldReduceMotion = useReducedMotion();

  // ---- debounce searchTerm -> debounced
  useEffect(() => {
    const t = setTimeout(() => setDebounced(searchTerm.trim()), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // ---- fetch
  const fetchData = useCallback(
    async (search = "") => {
      try {
        setLoading(true);
        const response = await contractService.get_all(search, token_client);
        setDocuments(response.data?.contracts || []);
      } catch (error) {
        toast.dismiss();
        toast.error(
          error?.response?.data?.message
            ? `Lỗi từ server: ${error.response.data.message}`
            : "Không thể kết nối tới server. Vui lòng kiểm tra mạng."
        );
        setDocuments([]);
      } finally {
        setLoading(false);
      }
    },
    [token_client]
  );

  // ---- trigger by URL search and debounced input
  useEffect(() => {
    const currentSearch = searchParams.get("search") || "";
    // nếu URL khác debounced -> ưu tiên URL (đồng bộ lần đầu / khi back-forward)
    if (currentSearch !== debounced) {
      setSearchTerm(currentSearch);
      setDebounced(currentSearch);
      fetchData(currentSearch);
    }
  }, []); // run once

  useEffect(() => {
    // mỗi lần debounced đổi -> update URL & fetch
    setSearchParams(debounced ? { search: debounced } : {});
    fetchData(debounced);
  }, [debounced, fetchData, setSearchParams]);

  const handleSearchClick = () => setDebounced(searchTerm.trim());
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchClick();
    }
  };
  const clearSearch = () => {
    setSearchTerm("");
    setDebounced("");
  };

  // ---- animations
  const listV = { hidden: {}, show: { transition: { staggerChildren: 0.05 } } };
  const itemV = {
    hidden: { opacity: 0, y: 6 },
    show: { opacity: 1, y: 0, transition: { duration: 0.18 } },
  };

  // ---- render helpers
  const highlight = useCallback((text, q) => {
    if (!q) return text;
    const parts = text.split(new RegExp(`(${escapeRegExp(q)})`, "gi"));
    return parts.map((p, i) =>
      p.toLowerCase() === q.toLowerCase() ? (
        <mark key={i} className="bg-yellow-200 rounded px-0.5">
          {p}
        </mark>
      ) : (
        <span key={i}>{p}</span>
      )
    );
  }, []);
  const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // ---- common search input
  const SearchBox = ({ className, rounded = "rounded-lg" }) => (
    <div className={`relative w-full flex items-center ${className || ""}`}>
      <input
        type="text"
        placeholder="Tìm kiếm hợp đồng / chứng từ"
        className={`border border-gray-200 ${rounded} px-4 py-2 focus:outline-blue-500 w-full pr-20`}
        value={searchTerm}
        autoComplete="off"
        onChange={(e) => setSearchTerm(e.target.value)}
        onKeyDown={handleKeyDown}
        aria-label="Tìm kiếm hợp đồng / chứng từ"
      />
      {searchTerm && (
        <button
          type="button"
          className="absolute right-12 text-gray-500 hover:text-gray-700"
          onClick={clearSearch}
          aria-label="Xoá tìm kiếm"
        >
          ✕
        </button>
      )}
      <button
        type="button"
        className="absolute right-3 cursor-pointer rounded-md p-1.5 hover:bg-gray-100 active:scale-[0.98]"
        onClick={handleSearchClick}
        aria-label="Tìm kiếm"
      >
        <img src="/icons/search-black.svg" alt="" />
      </button>
    </div>
  );

  // ---- skeleton
  const SkeletonItem = ({ className = "" }) => (
    <div
      className={`animate-pulse bg-white border border-[#EAEAEA] ${className} rounded-lg px-5 py-[13.5px]`}
    >
      <div className="flex items-center justify-between">
        <div className="flex gap-2 items-center">
          <div className="w-5 h-5 bg-gray-200 rounded" />
          <div className="h-4 w-48 bg-gray-200 rounded" />
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-gray-200 rounded" />
          <div className="h-4 w-24 bg-gray-200 rounded" />
        </div>
      </div>
    </div>
  );

  if (loading && !documents.length) {
    // giữ skeleton đầy màn thay vì spinner ở đây (trải nghiệm tốt hơn cho list)
    return isMobile ? (
      <div className="h-full w-full">
        <MobilePatternHeader
          title="Danh sách hợp đồng"
          onBack={() => navigate(-1)}
        />
        <div className="h-[calc(100%-64px)] bg-[#f2f2f2]">
          <div className="relative w-full flex items-center p-3">
            <SearchBox rounded="rounded-3xl" />
          </div>
          <div className="max-h-[calc(100%-165px)] overflow-auto p-3 space-y-4">
            {[...Array(6)].map((_, i) => (
              <SkeletonItem key={i} className="w-full" />
            ))}
          </div>
        </div>
      </div>
    ) : (
      <div className="h-full">
        <div className="flex justify-between items-center mb-4">
          <div className="flex gap-6 w-3/5">
            <SearchBox />
          </div>
        </div>
        <div className="space-y-4 max-h-[calc(100vh-200px)] h-full overflow-auto">
          {[...Array(8)].map((_, i) => (
            <SkeletonItem key={i} className="w-3/5" />
          ))}
        </div>
      </div>
    );
  }

  // =================== MOBILE ===================
  if (isMobile) {
    return (
      <motion.div className="h-full w-full" initial="hidden" animate="show">
        <MobilePatternHeader
          title="Danh sách hợp đồng"
          onBack={() => navigate(-1)}
        />

        <div className="h-[calc(100%-64px)] bg-[#f2f2f2]">
          <div className="relative w-full flex items-center p-3">
            <SearchBox rounded="rounded-3xl" />
          </div>

          <div className="max-h-[calc(100%-165px)] overflow-auto">
            <AnimatePresence mode="popLayout">
              <motion.div
                className="space-y-4  p-3"
                variants={listV}
                initial="hidden"
                animate="show"
                exit="hidden"
              >
                {documents.length > 0 ? (
                  documents.map((doc) => (
                    <motion.div
                      key={doc._id}
                      variants={itemV}
                      whileHover={shouldReduceMotion ? {} : { scale: 1.01 }}
                      whileTap={shouldReduceMotion ? {} : { scale: 0.99 }}
                      className="w-full bg-white rounded-lg flex items-center justify-between p-3 border border-[#EAEAEA] hover:bg-[#f9f9f9]"
                    >
                      <div className="flex gap-2 items-center pr-2">
                        <p className="text-gray-700 text-sm font-sf-medium line-clamp-2">
                          {highlight(doc.name || "—", debounced)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <img
                          src="/images/pdf-icon.png"
                          alt="PDF"
                          className="w-6 h-6"
                        />
                        <a
                          href={doc.pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                          aria-label={`Mở file PDF: ${doc.name}`}
                        >
                          Mở file PDF
                        </a>
                      </div>
                    </motion.div>
                  ))
                ) : (
                  <div className="flex items-center justify-center h-[50vh]">
                    <div className="text-center">
                      <img
                        src="/images/empty.png"
                        alt="Trống"
                        className="mx-auto mb-2"
                      />
                      <p className="text-gray-500 text-sm">
                        Không tìm thấy hợp đồng phù hợp.
                      </p>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    );
  }

  // =================== DESKTOP ===================
  return (
    <motion.div className="h-full" initial="hidden" animate="show">
      <div className="flex justify-between items-center mb-4">
        <div className="flex gap-6 w-3/5">
          <SearchBox />
        </div>
      </div>

      <div className="space-y-4 max-h-[calc(100vh-200px)] h-full overflow-auto">
        <AnimatePresence mode="popLayout">
          <motion.div
            className="space-y-4"
            variants={listV}
            initial="hidden"
            animate="show"
            exit="hidden"
          >
            {documents.length > 0 ? (
              documents.map((doc) => (
                <motion.div
                  key={doc._id}
                  variants={itemV}
                  whileHover={shouldReduceMotion ? {} : { scale: 1.005 }}
                  className="w-3/5 bg-white rounded-lg flex items-center justify-between px-5 py-[13.5px] border border-[#EAEAEA] hover:bg-[#f9f9f9]"
                >
                  <div className="flex gap-2 items-center">
                    <img
                      src="/images/document.png"
                      alt="Tài liệu"
                      className="w-5 h-5"
                    />
                    <p className="text-gray-700 text-sm font-sf-medium truncate">
                      {highlight(doc.name || "—", debounced)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <img
                      src="/images/pdf-icon.png"
                      alt="PDF"
                      className="w-6 h-6"
                    />
                    <a
                      href={doc.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                      aria-label={`Mở file PDF: ${doc.name}`}
                    >
                      Mở file PDF
                    </a>
                  </div>
                </motion.div>
              ))
            ) : (
              <p className="text-gray-500">
                Không có hợp đồng nào để hiển thị.
              </p>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default ListSignDocument;
